import { List as ImmutableList } from 'immutable';
import { useMemo } from 'react';

import type { TagProps } from 'app-components/flux/tags/tagsTypes';
import { mkTag } from 'app-components/flux/tags/tagsTypes';
import { transactionsTypes } from 'app-components/flux/transactions';
import type {
  Attachment,
  CashFlowTransaction,
  QcsColor,
  SplitProps,
  TransactionProps,
} from 'app-components/flux/transactions/transactionsTypes';
import {
  isAnySplitTransferCounterpart,
  preserveSplitTransferGuardedFields,
  reconcileSplitLinkedItems,
  stripDeadBillableLinks,
} from 'app-components/flux/transactions/splitsHelpers';
import type { LinkedInvoiceIndex } from 'app-components/flux/transactions/splitsHelpers';
import type { ChartOfAccountProps } from 'app-components/flux/chart-of-accounts/chartOfAccountsTypes';
import type { TransactionDetailsProjectSelect } from 'app-components/flux/transactions/transactionDetailsForm';

type Params = {
  accountId: string;
  amount: number;
  attachments: Attachment[] | null | undefined;
  businessId: string | null | undefined;
  checkNumber: string;
  coaRaw: ChartOfAccountProps | null | undefined;
  connectedTxn: CashFlowTransaction | undefined;
  customersAndProjects: TransactionDetailsProjectSelect;
  excludeF2S: boolean | null | undefined;
  excludeReports: boolean | null | undefined;
  isBill: boolean;
  isAssetPayment: boolean;
  isReviewed: boolean;
  /** Built from the current store, or `null` until the initial load completes (keeps the strip conservative). */
  linkedInvoiceIndex: LinkedInvoiceIndex | null;
  memo: string;
  originalTxnJS: TransactionProps;
  payee: string;
  postedOn: string;
  scheduledTransactionsById: Parameters<typeof isAnySplitTransferCounterpart>[0]['scheduledModelTxnsById'];
  splitRaw: SplitProps | null;
  status: 'CLEARED' | 'PENDING' | 'RECONCILED';
  tagsArr: TagProps[];
  transactionsById: Parameters<typeof isAnySplitTransferCounterpart>[0]['transactionsById'];
  userFlag: QcsColor | null | undefined;
};

export function useCurrentTransactionDraft({
  accountId,
  amount,
  attachments,
  businessId,
  checkNumber,
  coaRaw,
  connectedTxn,
  customersAndProjects,
  excludeF2S,
  excludeReports,
  isBill,
  isAssetPayment,
  isReviewed,
  linkedInvoiceIndex,
  memo,
  originalTxnJS,
  payee,
  postedOn,
  scheduledTransactionsById,
  splitRaw,
  status,
  tagsArr,
  transactionsById,
  userFlag,
}: Params): CashFlowTransaction {
  return useMemo(() => {
    const finalCoa = splitRaw ? null : coaRaw;
    const finalProjectId = splitRaw ? null : customersAndProjects.projectId;
    const finalCustomerId = splitRaw ? null : customersAndProjects.customerId;
    const finalIsBillable = isAssetPayment ? false : customersAndProjects.billable;
    const finalPostedOn = postedOn || originalTxnJS.postedOn;
    const formattedAmount = Number(amount?.toFixed?.(2));
    const finalMemo = originalTxnJS.memo !== '' && memo === '' ? undefined : memo;
    const finalTags = tagsArr.length > 0 ? ImmutableList(tagsArr.map((tag) => mkTag(tag))) : ImmutableList([]);
    // `linkedItem` is server-owned billable linkage (not user-editable here). Adopt the latest
    // server copy's link, then drop any link a live invoice/estimate no longer backs — so the draft
    // (used for display, the edit/delete lock, and recurring/rule saves) never carries a stale link.
    const cleanedSplitRaw = stripDeadBillableLinks(
      reconcileSplitLinkedItems(splitRaw, connectedTxn?.split),
      linkedInvoiceIndex,
    );
    const finalSplit = cleanedSplitRaw ? transactionsTypes.mkSplit(cleanedSplitRaw) : null;

    const newTxnObj: TransactionProps = {
      ...originalTxnJS,
      accountId,
      state: status,
      postedOn: finalPostedOn,
      payee,
      amount: formattedAmount,
      coa: finalCoa,
      businessId,
      customerId: finalCustomerId,
      projectId: finalProjectId,
      isBillable: finalIsBillable,
      tags: finalTags,
      isExcludedFromReports: excludeReports,
      isExcludedFromF2S: excludeF2S,
      memo: finalMemo,
      attachments,
      split: finalSplit,
      isBill,
      userFlag,
      isReviewed,
      check: { ...originalTxnJS.check, number: checkNumber },
    };

    const submittedTxn = transactionsTypes.mkCashFlowTransaction(newTxnObj);
    const hasSplitTransferCounterpart = isAnySplitTransferCounterpart({
      txn: submittedTxn,
      transactionsById,
      scheduledModelTxnsById: scheduledTransactionsById,
    });
    const previousTxn = connectedTxn ?? submittedTxn;

    return hasSplitTransferCounterpart
      ? preserveSplitTransferGuardedFields({ previous: previousTxn, next: submittedTxn })
      : submittedTxn;
  }, [
    accountId,
    amount,
    attachments,
    businessId,
    checkNumber,
    coaRaw,
    connectedTxn,
    customersAndProjects,
    excludeF2S,
    excludeReports,
    isBill,
    isAssetPayment,
    isReviewed,
    linkedInvoiceIndex,
    memo,
    originalTxnJS,
    payee,
    postedOn,
    scheduledTransactionsById,
    splitRaw,
    status,
    tagsArr,
    transactionsById,
    userFlag,
  ]);
}
